import NeptuneElement from '../../NeptuneElement.js';

class Datalist extends NeptuneElement {

    templateMarkup = `
        <div>
            <label></label>
            <input type="text" list="datalist-options" autocomplete="off" />
            <div>
                <span></span>
                <ul class="neptune-list-custom"></ul>
            </div>
            <datalist id="datalist-options">
                <slot></slot>
            </datalist>
        </div>
    `;

    componentCssUrl = '/src/components/form/datalist/datalist.css';

    constructor() {
        super();
    }

    #userErrorText = '';
    #internalErrorTextUpdate = false;
    #onInput = null;
    #onChange = null;
    #onBlur = null;

    async connectedCallback() {
        await super.connectedCallback();
        this.validateOptions();
        this.inputElement = this.shadowRoot.querySelector('input');
        this.#userErrorText = (this.getAttribute('error-text') || '').trim();
        this.handleAccessibilityAttributes();

        const input = this.inputElement;
        if (input) {
            Datalist.observedAttributes.forEach(attr => {
                this.processAttribute(attr, null, this.getAttribute(attr));
            });
        }

        this.populateOptions();
        this.processInitialAttributesAfterRender(Datalist.observedAttributes);
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['error', 'disabled', 'readonly', 'placeholder', 'value', 'label', 'supporting-text', 'required', 'error-text'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        if (!inputElement) {
            return;
        }

        switch (name) {
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                inputElement.disabled = isDisabled;
                break;
            }
            case 'readonly': {
                this.hasAttribute('readonly')
                    ? inputElement.setAttribute('readonly', '')
                    : inputElement.removeAttribute('readonly');
                break;
            }
            case 'error': {
                if (this.hasAttribute('error')) {
                    inputElement.setAttribute('aria-invalid', 'true');
                } else {
                    inputElement.removeAttribute('aria-invalid');
                }
                this.updateMessages();
                break;
            }
            case 'placeholder': {
                if (newValue) {
                    inputElement.placeholder = newValue;
                } else {
                    inputElement.removeAttribute('placeholder');
                }
                break;
            }
            case 'value': {
                inputElement.value = newValue || '';
                break;
            }
            case 'label': {
                this.setLabel(newValue);
                break;
            }
            case 'supporting-text': {
                this.setSupportingText(newValue);
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? inputElement.setAttribute('required', '')
                    : inputElement.removeAttribute('required');
                break;
            }
            case 'error-text': {
                if (!this.#internalErrorTextUpdate) {
                    this.#userErrorText = (newValue || '').trim();
                }
                this.updateMessages();
                break;
            }
            default: {
                break;
            }
        }
    }

    handleAccessibilityAttributes() {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        const labelElement = this.shadowRoot.querySelector('label');
        const supportingTextElement = this.shadowRoot.querySelector('span');
        const errorList = this.shadowRoot.querySelector('ul');

        if (!inputElement || !labelElement || !supportingTextElement || !errorList) {
            return;
        }

        const idBase = Math.random().toString(36).substr(2, 9);
        inputElement.id = `neptune-datalist-input-${idBase}`;
        labelElement.setAttribute('for', inputElement.id);
        supportingTextElement.id = `neptune-datalist-support-${idBase}`;
        errorList.id = `neptune-datalist-errors-${idBase}`;

        inputElement.setAttribute('aria-describedby', `${supportingTextElement.id} ${errorList.id}`);
    }

    setLabel(labelText) {
        const labelElement = this.shadowRoot.querySelector('label');
        if (!labelElement) {
            return;
        }
        labelElement.textContent = (labelText || '').trim();
    }

    setSupportingText(supportingText) {
        const supportingTextElement = this.shadowRoot.querySelector('span');
        if (!supportingTextElement) {
            return;
        }
        supportingTextElement.textContent = (supportingText || '').trim();
    }

    validateInput() {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        if (!inputElement) {
            return;
        }

        const label = (this.getAttribute('label') || 'Datalist').trim();
        const errors = new Set();
        const isRequiredAndEmpty = this.hasAttribute('required') && !inputElement.value.trim();

        if (isRequiredAndEmpty || inputElement.validity.valueMissing) {
            errors.add(this.#userErrorText || `${label} is required`);
        }

        const errorText = [...errors].join('|');

        this.#internalErrorTextUpdate = true;
        try {
            if (errorText) {
                this.setAttribute('error', '');
                this.setAttribute('error-text', errorText);
            } else {
                this.removeAttribute('error');
                this.removeAttribute('error-text');
            }
        } finally {
            this.#internalErrorTextUpdate = false;
        }

        this.updateMessages();
    }

    updateMessages() {
        const supportingTextElement = this.shadowRoot.querySelector('span');
        const errorList = this.shadowRoot.querySelector('ul');

        if (!supportingTextElement || !errorList) {
            return;
        }

        supportingTextElement.setAttribute('aria-live', 'polite');
        errorList.setAttribute('aria-live', 'polite');

        const supportingText = (this.getAttribute('supporting-text') || '').trim();
        supportingTextElement.textContent = supportingText;
        supportingTextElement.toggleAttribute('hidden', supportingText === '');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
            }
            errorList.toggleAttribute('hidden', errors.length === 0);

            if (supportingText) {
                supportingTextElement.classList.add('visually-hidden-a11y');
            } else {
                supportingTextElement.classList.remove('visually-hidden-a11y');
            }
            return;
        }

        errorList.innerHTML = '';
        errorList.setAttribute('hidden', '');
        supportingTextElement.classList.remove('visually-hidden-a11y');
    }

    setEventListeners() {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        if (!inputElement || this.#onInput || this.#onChange || this.#onBlur) {
            return;
        }

        this.#onInput = (e) => {
            const value = String(e.target.value ?? '');
            if (this.getAttribute('value') !== value) {
                this.setAttribute('value', value);
            }
        };

        this.#onChange = (e) => {
            const value = String(e.target.value ?? '');
            if (this.getAttribute('value') !== value) {
                this.setAttribute('value', value);
            }
            this.dispatchEvent(new CustomEvent('change', {
                detail: { value },
                bubbles: true,
                composed: true
            }));
        };

        this.#onBlur = () => this.validateInput();

        inputElement.addEventListener('input', this.#onInput);
        inputElement.addEventListener('change', this.#onChange);
        inputElement.addEventListener('blur', this.#onBlur);
    }

    removeEventListeners() {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        if (!inputElement) {
            return;
        }

        if (this.#onInput) {
            inputElement.removeEventListener('input', this.#onInput);
        }
        if (this.#onChange) {
            inputElement.removeEventListener('change', this.#onChange);
        }
        if (this.#onBlur) {
            inputElement.removeEventListener('blur', this.#onBlur);
        }

        this.#onInput = null;
        this.#onChange = null;
        this.#onBlur = null;
    }

    validateOptions() {
        const options = Array.from(this.querySelectorAll('option'));

        if (options.length === 0) {
            throw new Error('Invalid <neptune-datalist>: Datalist component must contain at least one <option> element.');
        }

        const hasValidOption = options.some(option => {
            const value = (option.getAttribute('value') || '').trim();
            return value !== '';
        });

        if (!hasValidOption) {
            throw new Error('Invalid <neptune-datalist>: Datalist component must contain at least one valid <option> with a value attribute.');
        }
    }

    populateOptions() {
        const datalistElement = this.shadowRoot.querySelector('datalist');
        if (!datalistElement) {
            return;
        }

        datalistElement.innerHTML = '';

        const options = Array.from(this.querySelectorAll('option'));
        options.forEach(option => {
            const clonedOption = option.cloneNode(true);
            datalistElement.appendChild(clonedOption);
        });
    }

    get value() {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        return inputElement ? inputElement.value : '';
    }

    set value(val) {
        const inputElement = this.inputElement || this.shadowRoot.querySelector('input');
        if (inputElement) {
            inputElement.value = val;
            this.setAttribute('value', val);
        }
    }
}

customElements.define('neptune-datalist', Datalist);
export default Datalist;